/* eslint-disable max-lines */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2.tsx';

import { useGetSubscriptions } from '@utils/api/resellers/use-get-reseller-subscriptions.tsx';
import { useCreateSubscriptionPlan } from '@utils/api/resellers/use-create-subscription-plan.tsx';
import { useGetSubscriptionPlans } from '@utils/api/resellers/use-get-subscription-plans.tsx';
import toast from 'react-hot-toast';
import { isAxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import { Plus, X } from 'lucide-react';
import { ResellerSubscription } from '@utils/api/resellers/use-get-subscriptions.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapPaymentModel = (v?: number, t?: any) =>
  (
    ({
      1: t?.('bundle') ?? 'Bundle',
      2: t?.('pay-as-you-go') ?? 'Pay-as-you-go',
      3: t?.('perpetual') ?? 'Perpetual'
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }) as any
  )[v ?? -1] ?? '-';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapBillingChannel = (v?: number, t?: any) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (({ 1: t?.('scriptix') ?? 'Scriptix', 2: t?.('azure') ?? 'Azure' }) as any)[
    v ?? -1
  ] ?? String(v ?? '-');

type Row = {
  id: number;
  sku: string;
  subscription_start: string | null;
  subscription_end: string | null;
  billing_channel_label: string;
  plan_payment_model_label: string;
  auto_renew: boolean;
};

type Props = { resellerId?: string };

export const SubscriptionDetails = ({ resellerId }: Props) => {
  const { t } = useTranslation();

  const [selectedPlanId, setSelectedPlanId] = useState<number>();
  const [autoRenew, setAutoRenew] = useState(false);
  const [addSubscription, setAddSubscription] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: subsData, isLoading } = useGetSubscriptions(resellerId || '');
  const { data: subscriptionData } = useGetSubscriptionPlans(debouncedQuery);
  const { getColour } = useThemeContext();

  const createSubscription = useCreateSubscriptionPlan();

  useEffect(() => {
    const tmr = setTimeout(() => setDebouncedQuery(searchText.trim()), 300);
    return () => clearTimeout(tmr);
  }, [searchText]);

  const safe = (d?: string | null) =>
    d && !Number.isNaN(new Date(d).getTime()) ? d : '-';

  const columns: DataGridColumn<Row>[] = [
    { id: 'id', header: t('id'), accessorKey: 'id', maxWidth: 100 },
    { id: 'sku', header: t('sku'), accessorKey: 'sku', minWidth: 160 },

    {
      id: 'subscription_start',
      header: t('subscription-start'),
      accessorKey: 'subscription_start',
      minWidth: 160,
      cell: ({ getValue }) => safe(getValue() as string)
    },
    {
      id: 'subscription_end',
      header: t('subscription-end'),
      accessorKey: 'subscription_end',
      minWidth: 160,
      cell: ({ getValue }) => safe(getValue() as string)
    },

    {
      id: 'billing_channel_label',
      header: t('billing-channel'),
      accessorKey: 'billing_channel_label',
      minWidth: 140
    },
    {
      id: 'plan_payment_model_label',
      header: t('plan-payment-model'),
      accessorKey: 'plan_payment_model_label',
      minWidth: 160
    },
    {
      id: 'auto_renew',
      header: t('auto-renew'),
      accessorKey: 'auto_renew',
      maxWidth: 120,
      cell: ({ getValue }) => ((getValue() as boolean) ? t('yes') : t('no'))
    }
  ];
  const rows: Row[] = useMemo(() => {
    const list = (subsData?.result as unknown as ResellerSubscription[]) ?? [];
    return list.map(sub => ({
      id: sub.id,
      sku: sub.sku,
      subscription_start: sub.subscription_start || '',
      subscription_end: sub.subscription_end || '',
      billing_channel_label: mapBillingChannel(sub.billing_channel, t),
      plan_payment_model_label: mapPaymentModel(sub.plan_payment_model, t),
      auto_renew: sub.auto_renew
    }));
  }, [subsData, t]);

  const handleAddSubscription = async () => {
    if (!selectedPlanId || !resellerId) return;
    try {
      setIsSubmitting(true);
      await createSubscription.mutateAsync({
        customer_id: Number(resellerId),
        auto_renew: autoRenew,
        plan_id: selectedPlanId
      });
      toast.success(t('subscription-plan-created-successfully'));
      setAddSubscription(false);
      setSelectedPlanId(undefined);
      setAutoRenew(false);
    } catch (error) {
      let message = t('an-error-occurred-while-creating-subscription-plan');
      if (isAxiosError<BaseError>(error)) {
        const d = error.response?.data;
        message =
          d?.error_description ||
          d?.detail ||
          d?.error ||
          d?.message ||
          message;
      }
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isPlanSubscribed = (plan: any) =>
    (subsData?.result ?? []).some(s => s.sku === plan.sku);

  return (
    <div className="p-4 space-y-2">
      <div className="flex justify-between items-center">
        <div />
        <Button
          onClick={() => setAddSubscription(true)}
          icon={<Plus width={14} />}
          size="small"
          label={t('add-subscription')}
          variant="secondary"
          className="w-fit"
        />
      </div>

      <Modal
        isOpen={addSubscription}
        onClose={() => setAddSubscription(false)}
        size="small"
        title={t('add-subscription')}
        footerActions={[
          <Button
            key="cancel"
            onClick={() => setAddSubscription(false)}
            label={t('cancel')}
            size="small"
            variant="smoke"
            disabled={isSubmitting}
          />,
          <Button
            key="add"
            onClick={handleAddSubscription}
            label={isSubmitting ? t('creating') : t('create')}
            size="small"
            variant="primary"
            disabled={!selectedPlanId || isSubmitting}
          />
        ]}
      >
        <div className="grid gap-6 px-2">
          {/* Select Plan Section */}
          <div className="space-y-3">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <SelectInput
                  id="subscription-plan"
                  label={t('select-plan')}
                  placeholder={t('search-plans')}
                  options={
                    subscriptionData?.result
                      .filter(plan => !isPlanSubscribed(plan))
                      .map(plan => ({
                        label: plan.description || plan.sku,
                        value: plan.id
                      })) ?? []
                  }
                  value={
                    selectedPlanId
                      ? {
                          label:
                            subscriptionData?.result.find(
                              p => p.id === selectedPlanId
                            )?.description ||
                            subscriptionData?.result.find(
                              p => p.id === selectedPlanId
                            )?.sku ||
                            '',
                          value: selectedPlanId
                        }
                      : null
                  }
                  onChange={option => {
                    setSelectedPlanId(
                      option?.value ? Number(option.value) : undefined
                    );
                  }}
                  onInputChange={setSearchText}
                  isSearchable
                  isClearable
                  size="small"
                />
              </div>
            </div>

            {/* Selected Plan Display */}
            {selectedPlanId && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      {subscriptionData?.result.find(
                        p => p.id === selectedPlanId
                      )?.description ||
                        subscriptionData?.result.find(
                          p => p.id === selectedPlanId
                        )?.sku}
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-300">
                      SKU:{' '}
                      {
                        subscriptionData?.result.find(
                          p => p.id === selectedPlanId
                        )?.sku
                      }
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedPlanId(undefined)}
                    className="p-1 hover:bg-blue-100 dark:hover:bg-blue-800 rounded-2xl"
                  >
                    <X className="h-4 w-4 text-blue-600 dark:text-blue-300" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Options Section */}
          <div
            className="border-t pt-4"
            style={{ borderColor: getColour('border') }}
          >
            <Checkbox
              id="autoRenew"
              name="autoRenew"
              label={t('automatic-renewal')}
              checked={autoRenew}
              onChange={e => setAutoRenew(e.target.checked)}
              variant="primary"
              wrapperClass="flex items-center gap-2"
              labelClass="text-sm font-medium"
            />
          </div>
        </div>
      </Modal>

      <div className="pt-2">
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          enableRowSelection={false}
          enablePagination
          manualPagination
          enableSorting={false}
          manualSorting={false}
          enableFiltering={false}
          manualFiltering={false}
          totalItems={subsData?.total_results ?? 0}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          showColumnFilters={false}
          filterMode="popover"
          height="55vh"
          dense
          grid={false}
          striped
          loading={isLoading || !subsData}
          getRowId={row => String(row.id)}
        />
      </div>
    </div>
  );
};
